// File: apps/web/src/stores/auth.store.ts

import { create } from 'zustand';
import { api, ApiResponse } from '../lib/api';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  displayName: string | null;
  avatar: string | null;
  role: string;
  emailVerified: boolean;
}

interface TwoFactorPending {
  userId: string;
  message: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  twoFactorPending: TwoFactorPending | null;
}

interface AuthActions {
  login: (email: string, password: string) => Promise<boolean>;
  register: (data: RegisterData) => Promise<void>;
  logout: () => Promise<void>;
  getCurrentUser: () => Promise<void>;
  clearError: () => void;
  completeTwoFactorLogin: (code: string) => Promise<void>;
  cancelTwoFactorLogin: () => void;
}

interface RegisterData {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  displayName?: string;
  role?: string;
}

type AuthStore = AuthState & AuthActions;

const initialState: AuthState = {
  user: null,
  accessToken: localStorage.getItem('accessToken'),
  refreshToken: localStorage.getItem('refreshToken'),
  isAuthenticated: !!localStorage.getItem('accessToken'),
  isLoading: false,
  error: null,
  twoFactorPending: null,
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...initialState,

  login: async (email: string, password: string): Promise<boolean> => {
    set({ isLoading: true, error: null, twoFactorPending: null });
    try {
      console.log('Making login request to /api/auth/login');
      const response = await api.post<ApiResponse>('/api/auth/login', {
        email,
        password,
      });

      console.log('Login response status:', response.status);
      console.log('Login response.data:', JSON.stringify(response.data, null, 2));

      if (!response.data?.data) {
        throw new Error('Invalid response format: ' + JSON.stringify(response.data));
      }

      // Check if 2FA is required
      if (response.data.data.requiresTwoFactor) {
        set({
          isLoading: false,
          twoFactorPending: {
            userId: response.data.data.userId,
            message: response.data.data.message,
          },
        });
        return true; // 2FA required
      }

      const { user, accessToken, refreshToken } = response.data.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      set({
        user,
        accessToken,
        refreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        twoFactorPending: null,
      });
      return false; // No 2FA required
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  register: async (data: RegisterData) => {
    set({ isLoading: true, error: null });
    try {
      const response = await api.post<ApiResponse>('/api/auth/register', data);

      const { user, accessToken, refreshToken } = response.data.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      set({
        user,
        accessToken,
        refreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Registration failed';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  logout: async () => {
    try {
      const { refreshToken } = get();
      if (refreshToken) {
        await api.post('/api/auth/logout', { refreshToken });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      set({
        user: null,
        accessToken: null,
        refreshToken: null,
        isAuthenticated: false,
        error: null,
      });
    }
  },

  getCurrentUser: async () => {
    if (!get().accessToken) {
      return;
    }

    set({ isLoading: true });
    try {
      const response = await api.get<ApiResponse>('/api/auth/me');
      set({ user: response.data.data, isLoading: false });
    } catch (error) {
      console.error('Failed to get current user:', error);
      set({ isLoading: false });
    }
  },

  clearError: () => set({ error: null }),

  completeTwoFactorLogin: async (code: string) => {
    const { twoFactorPending } = get();
    if (!twoFactorPending) {
      throw new Error('No 2FA verification pending');
    }

    set({ isLoading: true, error: null });
    try {
      const response = await api.post<ApiResponse>('/api/auth/2fa/verify', {
        userId: twoFactorPending.userId,
        code,
      });

      if (!response.data?.data) {
        throw new Error('Invalid response format');
      }

      const { user, accessToken, refreshToken } = response.data.data;

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);

      set({
        user,
        accessToken,
        refreshToken,
        isAuthenticated: true,
        isLoading: false,
        error: null,
        twoFactorPending: null,
      });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Invalid verification code';
      set({ isLoading: false, error: message });
      throw error;
    }
  },

  cancelTwoFactorLogin: () => {
    set({ twoFactorPending: null, error: null });
  },
}));
